import { find, map, propEq, reject, takeLast } from 'ramda';
import React, { createContext, FC, useCallback, useContext, useReducer, useRef } from 'react';
import { createHOCWithHookInside } from '../../hoc/createHOCWithHookInside';
import { uniqueRandomId } from '../../uniqueId';
import {
  AsyncNotificationStatus,
  INotification,
  INotificationAction,
  INotificatorContext,
  NotificationStateActionType,
  TNotificationOptions,
} from './useNotificator.types';

const DEFAULT_TIMEOUT = 5000;
const NOTIFICATION_SLOTS_COUNT = 1;
const INCREASED_NOTIFICATION_SLOTS_COUNT = 2; // mainly for double delete events from file awareness

const NotificatorContext = createContext<INotificatorContext>({
  notifications: [],
  notify: () => null,
  getNotificationById: () => null,
});

const notificationsReducer = (state: INotification[], action: INotificationAction) => {
  const {
    type,
    payload: { notification, status, id, NotificationComponent, increasedNotificationsSlots },
  } = action;

  switch (type) {
    case NotificationStateActionType.Add:
      return takeLast(increasedNotificationsSlots ? INCREASED_NOTIFICATION_SLOTS_COUNT : NOTIFICATION_SLOTS_COUNT, [...state, notification]);

    case NotificationStateActionType.Remove:
      return reject<INotification>(propEq('id', id), state);

    case NotificationStateActionType.SetMessage:
      return map<INotification, INotification>(
        (notification) =>
          propEq('id', id, notification)
            ? {
                ...notification,
                NotificationComponent,
              }
            : notification,
        state,
      );

    case NotificationStateActionType.SetStatus:
      return map<INotification, INotification>(
        (notification) =>
          propEq('id', id, notification)
            ? {
                ...notification,
                status,
              }
            : notification,
        state,
      );

    default:
      return state;
  }
};

export const NotificatorProvider: FC = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationsReducer, []);

  const getNotificationBy = useRef<(id: string) => INotification>();
  getNotificationBy.current = (id) => find<INotification>(propEq('id', id), notifications);

  const notify = useCallback((NotificationComponent, options: TNotificationOptions = {}) => {
    const { timeout = DEFAULT_TIMEOUT, isAsync = false, persist, increasedNotificationsSlots } = options;
    const id = uniqueRandomId('notification');

    let timeoutId;
    const hideByTimeout = (notification: INotification) => {
      if (persist) {
        return;
      }
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const updatedNotification = getNotificationBy.current(notification.id);
        if (updatedNotification && updatedNotification.status !== AsyncNotificationStatus.Pending) {
          updatedNotification.dismiss();
        }
      }, timeout);
    };

    const notification: INotification = {
      id,
      status: isAsync ? AsyncNotificationStatus.Pending : AsyncNotificationStatus.None,
      NotificationComponent,
      dismiss: () => dispatch({ type: NotificationStateActionType.Remove, payload: { id } }),
      setStatus: (status) => {
        dispatch({ type: NotificationStateActionType.SetStatus, payload: { id, status } });

        hideByTimeout(notification);
      },
      setMessage: (NotificationComponent) =>
        dispatch({ type: NotificationStateActionType.SetMessage, payload: { id, NotificationComponent } }),
    };

    dispatch({ type: NotificationStateActionType.Add, payload: { notification, increasedNotificationsSlots } });

    hideByTimeout(notification);

    return notification;
  }, []);

  return (
    <NotificatorContext.Provider value={{ notifications, getNotificationById: getNotificationBy.current, notify }}>
      {children}
    </NotificatorContext.Provider>
  );
};

export const useNotificatorConsumer = () => {
  const { notify, getNotificationById } = useContext(NotificatorContext);
  return { notify, getNotificationById };
};

export const useNotificatorRenderer = () => {
  const { notifications } = useContext(NotificatorContext);
  return { notifications };
};

/**
 * @deprecated use "useNotificatorConsumer"
 */
export const withNotificatorConsumer = createHOCWithHookInside('WithNotificatorConsumer', useNotificatorConsumer);
